import { COLORS } from '$gbusiness/enums';
import { defaultDataSource, defaultStyles, TableModel } from '$gbusiness/models/table';
import CellModel from '$gbusiness/models/cell';
import { currency } from '$gbusiness/helpers/util';
import { deriveRawToOrderDetails } from '$fbusiness/models/orderDetails';
import { accessHide, isAccessible } from '$fbusiness/helpers/util';

export const defaultInvoiceItemColumns = (settings) => [
  'sku',
  ...(settings?.showWarehouse ? ['warehouseId'] : []),
  ...(settings?.showVendor ? ['vendor.code'] : []),
];

export const ORDER_DETAILS_TABLE_CONFIG: TableModel = {
  dataSource: {
    ...defaultDataSource,
    deriveToModel: deriveRawToOrderDetails,
  },
  styles: {
    ...defaultStyles,
    color: COLORS.LIGHT,
    fontSize: '1.05em',
    minWidth: 500,
    maxWidth: 900,
    disableSort: false,
    cellPadding: ` 1px 4px`,
    responsive: true,
    isStripped: false,
    rowHeight: 22,
    headerHeight: 50,
  },
  blankText: (row, i) => <div>{!row.itemId && (row.subtotal || row.qty) ? '' : row.itemName}</div>,
};

export const exportItems = ({
  items,
  isInvoice,
  isFlagged,
  isRegenerated,
  hidePrice,
  ACCESS,
  currentState,
  showVendor = false,
  showWarehouse = false,
  showItemDescription = false,
}) => {
  const result = (items || []).map((row) => ({
    sku: row.sku,
    category: row.categoryStr,
    itemName: row.itemName,
    ...(showItemDescription && { itemDescription: row.itemDescription }),
    ...(row.modifiers?.length && { modifiers: row.modifiers.map((i) => i.name).join(', ') }),
    ...(row.note && { note: row.note }),
    ...(row.serials?.length > 0 && { serials: row.serials.join(',') }),
    ...(showWarehouse && { warehouse: row.warehouse ? row.warehouse.short || '' : '' }),
    ...(showVendor && { vendor: row.vendor?.code || '' }),
    ...(!isInvoice && { qtyOrdered: !row.itemId && !row.subtotal && !row.qty ? '' : row.qtyOrdered || row.qty }),
    ...(isRegenerated &&
      isAccessible(ACCESS.MONEY.VIEW, currentState) && {
        qtyStatus: `${row.totalReceived}/${row.totalSent}`,
      }),
    ...(!isFlagged && isInvoice && { qtySent: !row.itemId && !row.subtotal && !row.qty ? '' : row.qtySent }),
    ...(isRegenerated && { qty: `${row.qty}` }),
    ...(!hidePrice &&
      isAccessible(ACCESS.MONEY.VIEW, currentState) && {
        unitPrice: !row.itemId && !row.subtotal && !row.qty ? '' : currency(row.unitPrice),
      }),
    ...(!hidePrice &&
      isAccessible(ACCESS.MONEY.VIEW, currentState) && {
        price:
          !row.itemId && !row.subtotal && !row.qty ? '' : currency(isInvoice ? row.subtotal : row.unitPrice * row.qty),
      }),
  }));
  return result;
};

export const ORDER_DETAILS_TABLE_CONFIG_F = (itemId): TableModel => ({
  ...ORDER_DETAILS_TABLE_CONFIG,
  dataSource: {
    ...ORDER_DETAILS_TABLE_CONFIG.dataSource,
    pageSize: undefined,
  },
  styles: {
    ...ORDER_DETAILS_TABLE_CONFIG.styles,
    setRowClass: (row) => (row.itemId === itemId ? 'highlight' : ''),
  },
});

export const ORDER_DETAILS_TABLE = ({
  isInvoice,
  isFlagged,
  isRegenerated,
  hidePrice,
  ACCESS,
  showCategory = false,
  showVendor = false,
  showWarehouse = false,
  showItemDescription = false,
}): Array<CellModel> => [
  {
    label: 'SCREEN.ORDER.COLS.SKU',
    value: 'sku',
    component: (row) => <>{!row.itemId && !row.subtotal && !row.qty ? '' : row.sku}</>,
    width: 150,
  },
  {
    isHidden: !showCategory,
    label: 'SCREEN.ORDER.COLS.DEPTS',
    value: 'categoryStr',
    sortable: true,
    sortKey: 'categoryStr',
    component: (row) => <>{!row.itemId && !row.subtotal && !row.qty ? '' : row.categoryStr}</>,
    width: 150,
  },
  {
    label: 'SCREEN.ORDER.COLS.ITEM',
    value: 'itemName',
    component: (row) =>
      !row.itemId && !row.subtotal && !row.qty ? (
        <></>
      ) : (
        <div>
          <div>{row.itemName}</div>
          {showItemDescription && row.itemDescription && (
            <div className="description">{row.itemDescription}</div>
          )}
          {row.modifiers?.length > 0 && (
            <div className="mod">({row.modifiers.map((i) => i.name).join(', ')})</div>
          )}
          {row.note && <div className="mod">{`"${row.note}"`}</div>}
          {row.orderNote && <div className="mod">{`"${row.orderNote}"`}</div>}
          {row.serials?.length > 0 && <div className="serial-list">{`(${row.serials.join(', ')})`}</div>}
        </div>
      ),
    className: 'main',
    alwaysShow: true,
    width: 300,
  },
  {
    isHidden: !showWarehouse,
    label: 'SCREEN.ORDER.COLS.WAREHOUSE',
    value: 'warehouseId',
    component: (row) => {
      return <>{row.warehouse ? row.warehouse.short || '' : ''}</>;
    },
    sortable: true,
    sortKey: 'warehouse.short',
    width: 150,
  },
  {
    isHidden: !showVendor,
    label: 'SCREEN.ORDER.COLS.VENDOR',
    value: 'vendor.code',
    component: (row) => {
      return <>{!row.itemId && !row.subtotal && !row.qty ? '' : row.vendor?.code || ''}</>;
    },
    width: 150,
  },
  {
    isHidden: isInvoice,
    label: 'SCREEN.ORDER.COLS.QTY',
    value: 'qtyOrdered',
    align: 'center',
    component: (row) => {
      return <>{!row.itemId && !row.subtotal && !row.qty ? '' : row.qtyOrdered || row.qty}</>;
    },
    className: 'desktop',
    alwaysShow: true,
    width: 120,
  },
  {
    isHidden: !isRegenerated,
    label: 'SCREEN.INVOICES.COLS.QTY_STATUS_TOTAL',
    value: 'qtyStatus',
    className: accessHide(ACCESS.MONEY.VIEW) + ' invoice-print',
    component: (row) => <>{!row.itemId && !row.subtotal && !row.qty ? '' : `${row.totalReceived}/${row.totalSent}`}</>,
    align: 'center',
    alwaysShow: true,
    width: 120,
  },
  {
    isHidden: !isFlagged,
    label: 'SCREEN.INVOICES.COLS.QTY_STATUS',
    value: '',
    component: (row) => (
      <div
        className={row.qtyReceived !== row.qtySent ? 'error' : ''}>{`${row.qtyReceived}/${row.qtySent}`}</div>
    ),
    align: 'center',
    alwaysShow: true,
    width: 120,
  },
  {
    isHidden: isFlagged || !isInvoice,
    label: 'SCREEN.INVOICES.COLS.QTY',
    value: 'qtySent',
    component: (row) => <>{!row.itemId && !row.subtotal && !row.qty ? '' : row.qtySent}</>,
    align: 'center',
    alwaysShow: true,
    width: 120,
  },
  {
    isHidden: !isRegenerated,
    label: 'SCREEN.INVOICES.COLS.QTY_ADJUSTED',
    value: 'qty',
    align: 'center',
    className: 'invoice-print',
    alwaysShow: true,
    width: 120,
  },

  // {
  //   isHidden: !isInvoice,
  //   label: 'SCREEN.ORDER.COLS.QTY_SENT',
  //   value: 'totalSent',
  //   component: row => (hasProblem ? <>{`${row.qtySent}/${row.totalSent}`}</> : <>{row.totalSent}</>),
  //   align: 'center',
  //   className: 'desktop',
  //   width: 120,
  // },
  // {
  //   isHidden: !isFlagged,
  //   label: 'SCREEN.ORDER.COLS.QTY_RECEIVED',
  //   value: 'totalReceived',
  //   component: row => <>{`${row.qtyReceived}/${row.totalReceived}`}</>,
  //   align: 'center',
  //   className: 'desktop',
  //   width: 120,
  // },
  // ...(hasProblem
  //   ? [
  //       {
  //         label: 'SCREEN.SHIPPING_MODAL.COLS.QTY',
  //         value: 'qty',
  //         align: 'center',
  //         width: 200,
  //       },
  //     ]
  //   : []),
  {
    isHidden: hidePrice,
    label: 'SCREEN.ORDER.COLS.PRICE',
    align: 'right',
    value: 'price',
    className: `invoice-print ${accessHide(ACCESS.MONEY.VIEW)}`,
    alwaysShow: true,
    component: (row) => <>{!row.itemId && !row.subtotal && !row.qty ? '' : currency(row.unitPrice)}</>,
    width: 150,
  },
  {
    isHidden: hidePrice,
    label: 'SCREEN.ORDER.COLS.TOTAL',
    value: 'total',
    align: 'right',
    alwaysShow: true,
    className: `invoice-print ${accessHide(ACCESS.MONEY.VIEW)}`,
    component: (row) => (
      <>{!row.itemId && !row.subtotal && !row.qty ? '' : currency(isInvoice ? row.subtotal : row.unitPrice * row.qty)}</>
    ),
    width: 150,
    minWidth: 110,
    maxWidth: 110,
  },
];
